import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import crmKpiService from '../../services/crmKpiService';
import crmPipelineService from '../../services/crmPipelineService';
import type { CrmKpiDashboard, CrmPipeline } from '../../types/crm';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';

const KpiCard: React.FC<{ title: string; value: string; subtitle?: string }> = ({
  title,
  value,
  subtitle,
}) => (
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', mt: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CrmKpis: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [pipelineId, setPipelineId] = useState<number | ''>('');
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [data, setData] = useState<CrmKpiDashboard | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const pipes = await crmPipelineService.getAll();
        setPipelines(pipes ?? []);
      } catch {
        /* opcional */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const dashboard = await crmKpiService.getDashboard({
        pipelineId: pipelineId === '' ? undefined : pipelineId,
        nroVendedor: nroVendedor ?? undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
      });
      setData(dashboard);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar KPIs'), { variant: 'error' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [pipelineId, nroVendedor, fechaDesde, fechaHasta, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    try {
      const blob = await crmKpiService.exportCsv({
        pipelineId: pipelineId === '' ? undefined : pipelineId,
        nroVendedor: nroVendedor ?? undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-kpis-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Exportación iniciada', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al exportar CSV'), { variant: 'error' });
    }
  };

  const funnelData = (data?.funnel ?? [])
    .slice()
    .sort((a, b) => a.orden - b.orden)
    .map((f) => ({
      name: f.etapaNombre,
      cantidad: f.cantidad,
      monto: f.monto,
    }));

  const vendedorData = (data?.porVendedor ?? []).map((v) => ({
    name: v.nombreVendedor || `Vend. ${v.nroVendedor}`,
    ganados: v.cantidadGanados,
    perdidos: v.cantidadPerdidos,
    monto: v.montoCerrado,
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                KPIs comerciales
              </Typography>
              <Can permiso="crm.kpis">
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  disabled={loading}
                >
                  Exportar CSV
                </Button>
              </Can>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                select
                size="small"
                label="Pipeline"
                value={pipelineId}
                onChange={(e) => setPipelineId(e.target.value === '' ? '' : Number(e.target.value))}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {pipelines.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <VendedorSelect
                value={nroVendedor}
                onChange={setNroVendedor}
                allowEmpty
                emptyOptionLabel="Todos"
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                size="small"
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
              <TextField
                size="small"
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
              />
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  title="Pipeline abierto"
                  value={formatCurrency(data?.montoPipelineAbierto ?? 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  title="Forecast ponderado"
                  value={formatCurrency(data?.forecastPonderado ?? 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  title="Ganados"
                  value={String(data?.dealsGanados ?? 0)}
                  subtitle={formatCurrency(data?.montoGanado ?? 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  title="Perdidos"
                  value={String(data?.dealsPerdidos ?? 0)}
                  subtitle={formatCurrency(data?.montoPerdido ?? 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  title="Ciclo promedio"
                  value={
                    data?.cicloPromedioDias != null
                      ? `${Math.round(data.cicloPromedioDias)} días`
                      : '-'
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Embudo (cantidad por etapa)
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={funnelData} layout="vertical" margin={{ left: 24, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip />
                      <Bar dataKey="cantidad" name="Cantidad" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Por vendedor
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={vendedorData} margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="ganados" name="Ganados" fill="#2e7d32" />
                      <Bar dataKey="perdidos" name="Perdidos" fill="#d32f2f" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CrmKpis;
